import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/cote-azur/chauffeur-cote-azur.png";
import { cityPriceFaq } from "../slugs/cityPricing";

const vallaurisGolfeJuanEn: ChauffeurPage = {
  slug: "vallauris-golfe-juan",
  title: "VTC & private chauffeur in Vallauris Golfe-Juan",
  subtitle:
    "Private chauffeur in Vallauris Golfe-Juan: Nice airport transfers, the Golfe-Juan marina, Picasso ceramics and beaches, 7 days a week.",
  keywords: [
    "vtc golfe-juan",
    "private chauffeur vallauris",
    "vallauris golfe-juan airport transfer",
  ],
  hero: {
    title: "VTC & private chauffeur in Vallauris Golfe-Juan",
    subtitle:
      "Nice airport transfers, the Golfe-Juan marina, the ceramics town and beaches — available 24/7.",
    keywords: [
      "VTC Golfe-Juan",
      "Vallauris chauffeur",
      "Nice airport transfer",
      "Camille Rayon marina",
    ],
    imageSrc: heroImage.src,
    imageAlt: "VTC & private chauffeur in Vallauris Golfe-Juan",
    badges: ["Available 24/7", "Flight tracking", "Bilingual chauffeurs"],
  },
  benefits: {
    heading: "Why a chauffeur in Vallauris Golfe-Juan",
    items: [
      {
        title: "Nice airport, door to door",
        description:
          "Nice airport around 30 minutes away via the A8, with flight tracking and a personal welcome.",
      },
      {
        title: "Marina & beaches",
        description:
          "Camille Rayon marina, Midi and Soleil beaches: drop-off as close as possible, for crews, boaters and guests.",
      },
      {
        title: "Bilingual chauffeurs",
        description:
          "French/English, punctual and discreet, at ease between the potters’ town and the seafront.",
      },
      {
        title: "Local roots",
        description:
          "Based on the French Riviera, rated 5.0/5 from 38 Google reviews.",
      },
    ],
  },
  services: {
    heading: "Chauffeur services in Vallauris Golfe-Juan",
    services: [
      {
        title: "Nice airport transfers",
        text: "Pick-up with flight tracking to and from Nice Côte d’Azur airport and the stations.",
      },
      {
        title: "Marina & yachting",
        text: "Links with Camille Rayon marina and the Golfe-Juan seafront for crews and guests.",
      },
      {
        title: "Chauffeur at your disposal",
        text: "By the hour or the day, for the ceramics town, the workshops and the museums.",
      },
      {
        title: "Picasso & ceramics culture",
        text: "National Picasso Museum, Magnelli Museum and potters’ workshops, with drop-off in the heart of Vallauris.",
      },
      {
        title: "Long distance",
        text: "Any-distance trips from Golfe-Juan: Cannes, Antibes, Monaco, Saint-Tropez and Italy.",
      },
    ],
  },
  locations: {
    heading: "Places & pick-up points",
    items: [
      {
        name: "Camille Rayon marina",
        description: "The Golfe-Juan marina, quays and seafront.",
      },
      {
        name: "National Picasso Museum",
        description: "La Guerre et la Paix, in the Vallauris castle-museum.",
      },
      {
        name: "Ceramics town",
        description:
          "Potters’ workshops and boutiques in the heart of Vallauris.",
      },
      {
        name: "Midi & Soleil beaches",
        description: "Sandy beaches along Golfe-Juan.",
      },
      {
        name: "Napoleon’s landing",
        description:
          "Golfe-Juan, the historic starting point of the Route Napoléon.",
      },
      {
        name: "Nice airport",
        description: "About 30 minutes away via the A8 motorway.",
      },
    ],
  },
  generic: {
    heading: "A private chauffeur across Vallauris Golfe-Juan",
    subheading:
      "From the potters’ town to the marina, premium service between culture and sea.",
    paragraphs: [
      "KS Rent Services handles your journeys with a private chauffeur in Vallauris Golfe-Juan and across the French Riviera: transfers from Nice airport (about 30 minutes), marina links and long-distance trips.",
      "Two faces in one town: Vallauris, capital of ceramics shaped by Picasso, up on the heights, and Golfe-Juan, a seaside resort and marina at the water’s edge. Our bilingual chauffeurs link the two effortlessly.",
      "Between a visit to the National Picasso Museum, the potters’ market and lunch at Camille Rayon marina, your chauffeur strings the stops together and drops you as close as possible, with no parking worry.",
    ],
    bullets: [
      "Flight tracking & personal welcome",
      "Marina ⇄ potters’ town link",
      "Drop-off in the heart of Vallauris",
    ],
  },
  process: {
    heading: "Book a chauffeur in Vallauris Golfe-Juan",
    steps: [
      "Share your route, timings and number of passengers.",
      "We confirm the right vehicle and the fixed fare.",
      "Your chauffeur tracks your flight and greets you on time.",
      "Live adjustments for traffic and any last-minute changes.",
    ],
  },
  faq: [
    cityPriceFaq("en", "Vallauris Golfe-Juan"),
    {
      q: "How long is the transfer to Nice airport from Golfe-Juan?",
      a: "Around 30 minutes via the A8 motorway in normal conditions. We run transfers 24/7 with flight tracking and a personal welcome.",
    },
    {
      q: "Do you link with the Golfe-Juan marina?",
      a: "Yes, we reach Camille Rayon marina for crews, boaters and guests, with a welcome at the quay.",
    },
    {
      q: "Do your chauffeurs speak English?",
      a: "Yes, our chauffeurs are bilingual French/English and used to international guests.",
    },
    {
      q: "Which vehicles do you offer in Vallauris Golfe-Juan?",
      a: "Mercedes E-Class and S-Class sedans, 7-seat van, SUV and VIP minibus, depending on the number of passengers and the occasion.",
    },
    {
      q: "How far in advance should I book?",
      a: "Book as early as possible in high season and during the ceramics highlights; we also handle last-minute requests subject to availability.",
    },
    {
      q: "Do you take visitors on the trail of Picasso and ceramics?",
      a: "Yes, National Picasso Museum, Magnelli Museum and potters’ workshops, with drop-off in the heart of town and waiting on site.",
    },
  ],
  relatedLinks: [
    { label: "Private chauffeur in Cannes", href: "/en/chauffeur/cannes" },
    { label: "Nice chauffeur rates", href: "/en/chauffeur/nice" },
    { label: "Transfers from Nice Airport", href: "/en/chauffeur/airport" },
    {
      label: "Private driver on the French Riviera",
      href: "/en/chauffeur/french-riviera",
    },
  ],
  cta: {
    title: "Your private chauffeur in Vallauris Golfe-Juan",
    text: "Book your transfer or hourly hire — fast reply, 7 days a week.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book a chauffeur",
  },
  seo: {
    title: "Private chauffeur in Vallauris Golfe-Juan — car service 24/7",
    description:
      "Private car service in Vallauris Golfe-Juan: Nice airport transfer (~30 min), Camille Rayon marina, ceramics and Picasso museum. 5.0/5 from 38 reviews.",
    canonical: "/en/chauffeur/vallauris-golfe-juan",
  },
};

export default vallaurisGolfeJuanEn;
